import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTradingEvents } from '@/hooks/useTradingEvents';
import type { TradingEvent } from '@/services/tradingEventsApi';

const MAX_EVENTS = 300;

type TradingEventLogContextValue = {
  events: TradingEvent[];
  clearEvents: () => void;
};

const TradingEventLogContext = createContext<TradingEventLogContextValue | null>(null);

export function TradingEventLogProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<TradingEvent[]>([]);

  const pushEvent = useCallback((event: TradingEvent) => {
    setEvents((prev) => [...prev, event].slice(-MAX_EVENTS));
  }, []);

  useTradingEvents(pushEvent);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const value = useMemo(
    () => ({
      events,
      clearEvents,
    }),
    [clearEvents, events],
  );

  return <TradingEventLogContext.Provider value={value}>{children}</TradingEventLogContext.Provider>;
}

export function useTradingEventLog(): TradingEventLogContextValue {
  const ctx = useContext(TradingEventLogContext);
  if (!ctx) {
    throw new Error('useTradingEventLog must be used within TradingEventLogProvider');
  }
  return ctx;
}
